import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { focusControl, getEditableControls } from '../../../a11y/focus-utils';
import { HxBaseControl } from '../../../base/hx-base-control';
import { DebounceDirective, StopPropagationDirective } from '../../../directives';
import { EllipsisTooltipDirective } from '../../../directives/ellipsis-tooltip';
import { HxCheckbox } from '../../checkbox/checkbox';
import { HxIcon } from '../../icon/icon';
import { HxIconButton } from '../../icon-button/icon-button';
import { HxInput } from '../../input';
import { HxOptionGroup } from '../shared/option-group.model';
import { HxOption } from '../shared/option.model';

/** Incrementing counter used to build unique DOM ids for dropdown panels/options. */
let dropdownIdCounter = 0;

/**
 * A simplified dropdown select component with essential single-select functionality and optional search.
 *
 * @example
 *   ```html
 *   <hx-dropdown
 *     [options]="countries"
 *     [placeholder]="'Select a country'"
 *     [searchable]="true"
 *     (valueChange)="onCountryChange($event)">
 *   </hx-dropdown>
 *   ```;
 *
 * @component HxDropdown
 * @implements ControlValueAccessor
 */
@Component({
  selector: 'hx-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    PortalModule,
    ScrollingModule,
    HxInput,
    HxCheckbox,
    ReactiveFormsModule,
    StopPropagationDirective,
    HxIcon,
    HxIconButton,
    DebounceDirective,
    EllipsisTooltipDirective,
  ],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.css', '../select-tokens.css', './dropdown-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxDropdownComponent),
      multi: true,
    },
  ],
  host: {
    '[attr.data-hx-search-activation]': 'searchable() ? "" : null',
    '(hx-search-activate)': 'activateSearch($event)',
    '[class]': 'hostClasses()',
    '[attr.data-disabled]': 'disabled()',
    '[attr.data-open]': 'isOpen()',
  },
})
export class HxDropdownComponent<T = any> extends HxBaseControl {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private overlayRef: OverlayRef | null = null;

  // ============================================================================
  // INPUTS - Component Configuration
  // ============================================================================

  /** Array of options or option groups to display */
  readonly options = input<(HxOption<T> | HxOptionGroup<T>)[]>([]);

  /**
   * Current value of the dropdown (for direct binding without forms)
   *
   * @default null
   */
  readonly value = input<T | T[] | null | undefined>(undefined);

  /** Effect that syncs the value input with internalValue signal */
  private readonly valueEffect = effect(() => {
    const val = this.value();
    if (val !== undefined) {
      this.internalValue.set(val);
    }
  });

  /**
   * Custom display text to override the default selected option label. When provided, this text is shown in the trigger
   * instead of the selected option's label. Useful for cascading/hierarchical dropdowns that need to show a path.
   *
   * @default undefined
   */
  readonly customDisplayText = input<string | undefined>(undefined);

  /**
   * Custom display subtitle to override the default selected option subtitle.
   *
   * @default undefined
   */
  readonly customDisplaySubtitle = input<string | undefined>(undefined);

  /**
   * Component size variant
   *
   * @default 'md'
   */
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('md');

  /** Custom CSS classes for styling */
  readonly class = input<string>('');

  /** Custom CSS classes applied to the dropdown overlay panel. */
  readonly panelClass = input<string>('');

  /**
   * Enable search/filter functionality
   *
   * @default false
   */
  readonly searchable = input<boolean>(false);

  /**
   * Allow committing free-text values (single-select only).
   *
   * When enabled, values not present in `options` are still displayed and can be committed from the search box.
   *
   * @default false
   */
  readonly allowFreeText = input<boolean>(false);

  /**
   * Enable multiple selection mode
   *
   * @default false
   */
  readonly multiple = input<boolean>(false);

  /**
   * Show "Select All" checkbox in multi-select mode
   *
   * @default false
   */
  readonly showSelectAll = input<boolean>(false);

  /**
   * Enable virtual scrolling for large lists
   *
   * @default false
   */
  readonly virtualScroll = input<boolean>(false);

  /**
   * Item height in pixels for virtual scrolling
   *
   * @default 40
   */
  readonly itemSize = input<number>(40);

  /**
   * Whether the dropdown is required
   *
   * @default false
   */
  readonly required = input<boolean>(false);

  /**
   * Hide the trigger chevron icon (e.g. when there is only a single option to choose from)
   *
   * @default false
   */
  readonly hideChevron = input<boolean>(false);

  /**
   * Whether the dropdown is disabled
   *
   * @default false
   */
  override readonly disabled = input<boolean | undefined>(false);

  /**
   * Whether to show the ellipsis tooltip on the trigger label when text is truncated
   *
   * @default true
   */
  readonly showTriggerTooltip = input<boolean | undefined>(true);

  // ============================================================================
  // OUTPUTS - Event Emitters
  // ============================================================================

  /** Emitted when the selected value changes (T | null for single, T[] | null for multi) */
  readonly valueChange = output<T | T[] | null>();

  /** Emitted when the dropdown opens */
  readonly opened = output<void>();

  /** Emitted when the dropdown closes */
  readonly closed = output<void>();

  /** Emitted when focus leaves the dropdown (e.g., clicking outside) */
  readonly blur = output<void>();

  /** Emitted when search query changes */
  readonly searchChange = output<string>();

  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  /** Reference to the trigger button element */
  private readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerRef');

  /** Template reference for the dropdown panel */
  private readonly dropdownTemplate = viewChild<TemplateRef<any>>('dropdownPortal');

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** Current value of the dropdown (T | null for single, T[] for multi) */
  protected readonly internalValue = signal<T | T[] | null>(null);

  /** Whether the dropdown is currently open */
  protected readonly isOpen = signal<boolean>(false);

  /** Index of the currently focused option (for keyboard navigation) */
  protected readonly focusedIndex = signal<number>(-1);

  /** Unique id used to namespace the panel and option DOM ids (for `aria-activedescendant`). */
  protected readonly panelId = `hx-dropdown-${++dropdownIdCounter}`;

  /** Options that can actually be activated with the keyboard (visible and not disabled) */
  protected readonly navigableOptions = computed(() => this.filteredOptions().filter((opt) => !opt.disabled));

  /** The option currently highlighted by the keyboard, if any */
  protected readonly activeOption = computed<HxOption<T> | null>(() => {
    const index = this.focusedIndex();
    const options = this.navigableOptions();
    return index >= 0 && index < options.length ? (options[index] ?? null) : null;
  });

  /** Search query for filtering options */
  protected readonly searchQuery = signal<string>('');

  /** Flattened array of all options (including from groups) */
  protected readonly flatOptions = computed(() => {
    const opts = this.options();
    const flat: HxOption<T>[] = [];

    opts.forEach((item) => {
      if ('options' in item) {
        flat.push(...(item as HxOptionGroup<T>).options);
      } else {
        flat.push(item as HxOption<T>);
      }
    });

    return flat;
  });

  /** Filtered options based on search query */
  protected readonly filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query || !this.searchable()) {
      return this.flatOptions();
    }

    return this.flatOptions().filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        (opt.subtitle && opt.subtitle.toLowerCase().includes(query)) ||
        (typeof opt.value === 'string' && opt.value.toLowerCase().includes(query))
    );
  });

  /** Filtered options with groups preserved (for rendering) */
  protected readonly displayOptions = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query || !this.searchable()) {
      return this.options();
    }

    const opts = this.options();
    const filtered: (HxOption<T> | HxOptionGroup<T>)[] = [];

    opts.forEach((item) => {
      if ('options' in item) {
        // Group: filter its options
        const group = item as HxOptionGroup<T>;
        const matchingOptions = group.options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(query) ||
            (opt.subtitle && opt.subtitle.toLowerCase().includes(query)) ||
            (typeof opt.value === 'string' && opt.value.toLowerCase().includes(query))
        );
        if (matchingOptions.length > 0) {
          filtered.push({ ...group, options: matchingOptions });
        }
      } else {
        // Single option
        const opt = item as HxOption<T>;
        if (
          opt.label.toLowerCase().includes(query) ||
          (opt.subtitle && opt.subtitle.toLowerCase().includes(query)) ||
          (typeof opt.value === 'string' && opt.value.toLowerCase().includes(query))
        ) {
          filtered.push(opt);
        }
      }
    });

    return filtered;
  });

  /** Selected option object (single mode) or array of option objects (multi mode) */
  protected readonly selectedOption = computed(() => {
    const value = this.internalValue();
    const options = this.flatOptions();

    if (this.multiple()) {
      // Multi-select mode: return array of matching options
      if (!Array.isArray(value)) return [];
      return options.filter((opt) => value.includes(opt.value));
    } else {
      // Single-select mode: return single option or null
      return options.find((opt) => opt.value === value) || null;
    }
  });

  /** Display text for the trigger button */
  protected readonly displayText = computed(() => {
    // If custom display text is provided, use it
    const customText = this.customDisplayText();
    if (customText !== undefined && customText.length > 0) {
      return customText;
    }

    const selected = this.selectedOption();

    if (this.multiple()) {
      // Multi-select mode
      const selectedArray = selected as HxOption<T>[];
      return selectedArray.length === 0
        ? this.placeholder()
        : selectedArray.length === 1
          ? selectedArray[0]?.label || ''
          : `${selectedArray.length} items selected`;
    } else {
      // Single-select mode
      const singleOption = selected as HxOption<T> | null;

      if (singleOption?.label) {
        return singleOption.label;
      }

      const value = this.internalValue();
      if (this.allowFreeText() && !this.multiple() && typeof value === 'string' && value.trim().length > 0) {
        return value;
      }

      return this.placeholder();
    }
  });

  /** Display subtitle for the trigger button */
  protected readonly displaySubtitleText = computed(() => {
    const customSubtitle = this.customDisplaySubtitle();
    if (customSubtitle !== undefined) {
      return customSubtitle;
    }

    const selected = this.selectedOption();
    if (!this.multiple() && selected) {
      return (selected as HxOption<T>).subtitle || '';
    }
    return '';
  });

  /** Whether free-text mode is enabled (single-select only) */
  protected readonly freeTextMode = computed(() => this.allowFreeText() && !this.multiple());

  /** Whether current search query can be offered as a free-text option */
  protected readonly showFreeTextOption = computed(() => {
    if (!this.freeTextMode() || !this.searchable()) {
      return false;
    }

    const query = this.searchQuery().trim();
    if (!query) {
      return false;
    }

    const lowerQuery = query.toLowerCase();
    const hasExactMatch = this.flatOptions().some((opt) => {
      if (opt.label.toLowerCase() === lowerQuery) return true;
      return typeof opt.value === 'string' && opt.value.toLowerCase() === lowerQuery;
    });

    return !hasExactMatch;
  });

  /** Whether a value is currently selected */
  protected readonly hasValue = computed(() => {
    const value = this.internalValue();
    if (this.multiple()) {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  });

  /** All available option values for Select All */
  protected readonly allOptionValues = computed(() => {
    return this.flatOptions()
      .filter((opt) => !opt.disabled)
      .map((opt) => opt.value);
  });

  /** Whether all options are selected */
  protected readonly allSelected = computed(() => {
    const value = this.internalValue();
    const allValues = this.allOptionValues();
    if (!Array.isArray(value) || allValues.length === 0) return false;
    return allValues.every((v) => value.includes(v));
  });

  /** Whether some (but not all) options are selected */
  protected readonly someSelected = computed(() => {
    const value = this.internalValue();
    const allValues = this.allOptionValues();
    if (!Array.isArray(value) || value.length === 0) return false;
    return value.length > 0 && value.length < allValues.length;
  });

  /** Computed CSS classes for the host element */
  protected readonly hostClasses = computed(() => {
    const classes = ['hx-dropdown'];

    classes.push(`hx-dropdown--${this.size()}`);

    if (this.error()) {
      classes.push('hx-dropdown--error');
    }
    if (this.disabled()) {
      classes.push('hx-dropdown--disabled');
    }
    if (this.readonly()) {
      classes.push('hx-dropdown--readonly');
    }
    if (this.isOpen()) {
      classes.push('hx-dropdown--open');
    }
    if (this.hasValue()) {
      classes.push('hx-dropdown--has-value');
    }

    const custom = this.class();
    if (custom) {
      classes.push(custom);
    }

    return classes.join(' ');
  });

  /** Computed CSS classes for the panel */
  protected readonly panelClasses = computed(() => {
    const classes = ['hx-dropdown-panel'];
    classes.push(`hx-dropdown-panel--${this.size()}`);
    const customPanelClass = this.panelClass();
    if (customPanelClass) {
      classes.push(customPanelClass);
    }
    return classes.join(' ');
  });

  /** Math reference for template */
  protected readonly Math = Math;

  /** TrackBy function for virtual scroll */
  protected trackByFn(_index: number, item: HxOption<T> | HxOptionGroup<T>): string | number {
    return item.id;
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  constructor() {
    super();
    // Register as value accessor manually to avoid circular dependency
    // (NG_VALUE_ACCESSOR provider + NgControl injection causes circular DI)
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    // Clean up overlay on destroy
    this.destroyRef.onDestroy(() => {
      this.closeDropdown();
      this.overlayRef?.dispose();
    });
  }

  // ============================================================================
  // CONTROL VALUE ACCESSOR
  // ============================================================================

  private onChange: (value: T | T[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: T | T[] | null): void {
    if (this.multiple()) {
      // Multi-select mode: drop empty values so '' does not count as a selection.
      this.internalValue.set(
        (Array.isArray(value) ? value : value === null || value === undefined ? [] : [value]).filter(
          (v) => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== '')
        ) as T[]
      );
    } else {
      // Single-select mode: ensure value is T | null
      this.internalValue.set(Array.isArray(value) ? value[0] || null : value);
    }
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Disabled state is controlled via input
  }

  // ============================================================================
  // DROPDOWN MANAGEMENT
  // ============================================================================

  /** Toggle dropdown open/closed */
  protected toggleDropdown(): void {
    if (this.disabledState() || this.readonly() || this.hideChevron()) {
      return;
    }

    if (this.isOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  activateSearch(event: Event): void {
    if (
      this.destroyRef.destroyed ||
      !this.searchable() ||
      this.disabledState() ||
      this.readonly() ||
      this.hideChevron()
    ) {
      return;
    }
    event.preventDefault();
    if (this.isOpen()) {
      this.focusPanel();
    } else {
      this.openDropdown();
    }
  }

  /** Open the dropdown */
  protected openDropdown(): void {
    if (this.disabled() || this.readonly() || this.isOpen() || this.hideChevron()) {
      return;
    }

    const trigger = this.triggerButton();
    const template = this.dropdownTemplate();

    if (!trigger || !template) {
      return;
    }

    // Create overlay if it doesn't exist
    if (!this.overlayRef) {
      const positionStrategy = this.createPositionStrategy();

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        width: trigger.nativeElement.offsetWidth,
      });

      // Close on backdrop click
      this.overlayRef
        .backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.closeDropdown());
    }

    // Always sync panel width to the current trigger button width on every open
    const triggerWidth = trigger.nativeElement.offsetWidth;
    this.overlayRef.updateSize({ width: triggerWidth, minWidth: triggerWidth });

    // Create and attach TemplatePortal
    const portal = new TemplatePortal(template, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isOpen.set(true);
    const options = this.navigableOptions();
    const selectedIndex = options.findIndex((option) => this.isOptionSelected(option));
    this.focusedIndex.set(selectedIndex >= 0 ? selectedIndex : options.length > 0 ? 0 : -1);
    this.focusPanel();
    this.opened.emit();
  }

  /** Close the dropdown */
  protected closeDropdown(): void {
    if (!this.isOpen()) {
      return;
    }

    this.overlayRef?.detach();
    this.isOpen.set(false);
    this.focusedIndex.set(-1);
    this.onTouched();

    // Auto-clear search text when dropdown closes for fresh start on next open
    this.searchQuery.set('');
    this.searchChange.emit('');

    this.blur.emit();
    this.closed.emit();

    // Return focus to trigger
    const trigger = this.triggerButton();
    if (trigger) {
      trigger.nativeElement.focus();
    }
  }

  /** Create CDK position strategy based on position input */
  private createPositionStrategy(): FlexibleConnectedPositionStrategy {
    const trigger = this.triggerButton();
    if (!trigger) {
      throw new Error('Trigger element not found');
    }

    const positions = this.getPositions();

    return this.overlay
      .position()
      .flexibleConnectedTo(trigger.nativeElement)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withViewportMargin(8);
  }

  /** Get CDK positions for dropdown panel */
  private getPositions(): ConnectedPosition[] {
    return [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];
  }

  // ============================================================================
  // OPTION SELECTION
  // ============================================================================

  /** Select an option */
  protected selectOption(option: HxOption<T>): void {
    if (option.disabled) {
      return;
    }

    if (this.multiple()) {
      // Multi-select mode: toggle selection
      this.toggleMultiOption(option.value);
      // Keep dropdown open for multi-select
    } else {
      // Single-select mode: replace value and close
      this.updateValue(option.value);
      this.closeDropdown();
    }
  }

  /** Clear the current selection */
  clearSelection(): void {
    if (this.multiple()) {
      this.updateValue([]);
    } else {
      this.updateValue(null);
    }
    this.closeDropdown();
  }

  /** Select all non-disabled options */
  protected selectAll(): void {
    if (!this.multiple()) return;
    this.updateValue(this.allOptionValues());
  }

  /** Deselect all options */
  protected deselectAll(): void {
    if (!this.multiple()) return;
    this.updateValue([]);
  }

  /** Toggle selection of an option in multi-select mode */
  private toggleMultiOption(value: T): void {
    const currentValue = this.internalValue();
    let newValue: T[];

    if (Array.isArray(currentValue)) {
      const index = currentValue.indexOf(value);
      if (index > -1) {
        // Remove from selection
        newValue = currentValue.filter((v) => v !== value);
      } else {
        // Add to selection
        newValue = [...currentValue, value];
      }
    } else {
      // Initialize array with first selection
      newValue = [value];
    }

    this.updateValue(newValue);
  }

  /** Check if an option is selected */
  protected isOptionSelected(option: HxOption<T>): boolean {
    const value = this.internalValue();

    if (this.multiple()) {
      return Array.isArray(value) && value.includes(option.value);
    } else {
      return value === option.value;
    }
  }

  /** Get CSS classes for an option */
  protected getOptionClasses(option: HxOption<T>, index: number): string {
    const classes = ['hx-dropdown-option'];

    if (this.isOptionSelected(option)) {
      classes.push('hx-dropdown-option--selected');
    }
    if (option.disabled) {
      classes.push('hx-dropdown-option--disabled');
    }
    if (this.activeOption()?.id === option.id) {
      classes.push('hx-dropdown-option--focused');
    }
    if (option.className) {
      classes.push(option.className);
    }

    return classes.join(' ');
  }

  /** Stable DOM id for an option, used for `aria-activedescendant` and scroll-into-view. */
  protected optionDomId(option: HxOption<T>): string {
    return `${this.panelId}-option-${option.id}`;
  }

  /** DOM id of the option currently highlighted by the keyboard. */
  protected readonly activeOptionDomId = computed(() => {
    const option = this.activeOption();
    return option ? this.optionDomId(option) : null;
  });

  // ============================================================================
  // KEYBOARD NAVIGATION
  // ============================================================================

  /** Handle keyboard events on trigger */
  protected onTriggerKeyDown(event: KeyboardEvent): void {
    if (this.disabledState() || this.readonly()) {
      return;
    }

    // The trigger can still own focus while the panel is open: the panel only takes focus on the next animation
    // frame, and closing returns focus here. Delegate so the panel keys work from either element.
    if (this.isOpen()) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Home':
        case 'End':
        case 'Enter':
        case ' ':
          this.onPanelKeyDown(event);
          // Guard the form-submit / page-scroll default even when there was no option to commit.
          event.preventDefault();
          return;
        case 'Escape':
        case 'Tab':
          this.onPanelKeyDown(event);
          return;
      }
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.openDropdown();
        }
        return;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        return;
      case 'Tab':
        // Nothing is open, so let the browser move to the next control naturally.
        return;
    }

    // Typing a printable character opens the dropdown and seeds the search, so typing "T" immediately filters the
    // list down to the entries matching "T".
    if (this.isPrintableKey(event) && this.searchable() && !this.isOpen()) {
      event.preventDefault();
      this.openDropdown();
      this.setSearchQuery(event.key);
    }
  }

  /** Handle keyboard events in dropdown panel */
  protected onPanelKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        return;
      case 'Home':
        event.preventDefault();
        this.focusedIndex.set(0);
        return;
      case 'End':
        event.preventDefault();
        this.focusedIndex.set(Math.max(0, this.navigableOptions().length - 1));
        return;
      case 'Enter':
      case ' ':
        if (this.commitActiveOption()) {
          event.preventDefault();
        }
        return;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        return;
      case 'Tab':
        if (event.shiftKey && this.searchable()) {
          event.preventDefault();
          const target = this.searchQuery()
            ? this.overlayRef?.overlayElement?.querySelector<HTMLElement>('.hx-dropdown-search__clear-btn')
            : this.overlayRef?.overlayElement?.querySelector<HTMLInputElement>('.hx-dropdown-search input');
          target?.focus();
          return;
        }
        this.handleTabOut(event);
        return;
    }
  }

  /**
   * Selects the option currently highlighted by the keyboard.
   *
   * Single-select closes the panel and returns focus to the trigger; multi-select keeps the panel open so further
   * options can be toggled with the arrow keys and Enter.
   *
   * @returns Whether an option was committed.
   */
  private commitActiveOption(): boolean {
    const option = this.activeOption();
    if (!option) {
      return false;
    }
    this.selectOption(option);
    return true;
  }

  /**
   * Closes the dropdown and lets focus continue to the next/previous editable control on the page.
   *
   * Selections made while the panel was open have already been written through `updateValue`, so they are preserved.
   */
  private handleTabOut(event: KeyboardEvent): void {
    event.preventDefault();

    const trigger = this.triggerButton()?.nativeElement ?? null;
    this.closeDropdown();

    if (!trigger) {
      return;
    }

    // Replay the key press from the (now focused) trigger so the surrounding navigation strategy decides where focus
    // goes. Inside a grid that keeps Tab following the column order instead of the flat document order.
    trigger.focus();
    const forwarded = new KeyboardEvent('keydown', {
      key: 'Tab',
      shiftKey: event.shiftKey,
      bubbles: true,
      cancelable: true,
    });
    trigger.dispatchEvent(forwarded);
    if (forwarded.defaultPrevented) {
      return;
    }

    const controls = getEditableControls(document.body);
    const currentIndex = controls.indexOf(trigger);
    if (currentIndex === -1) {
      return;
    }

    const next = controls[currentIndex + (event.shiftKey ? -1 : 1)];
    if (next) {
      focusControl(next);
    }
  }

  /** Move the keyboard highlight by `delta`, wrapping around the filtered list. */
  private moveFocus(delta: number): void {
    const count = this.navigableOptions().length;
    if (count === 0) {
      this.focusedIndex.set(-1);
      return;
    }

    const current = this.focusedIndex();
    const next = current < 0 ? (delta > 0 ? 0 : count - 1) : (current + delta + count) % count;
    this.focusedIndex.set(next);
  }

  /** Whether the key press represents a single printable character. */
  private isPrintableKey(event: KeyboardEvent): boolean {
    return event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey && event.key !== ' ';
  }

  /** Focus the search box (or the panel itself when not searchable) once the overlay is attached. */
  private focusPanel(): void {
    const panel = this.overlayRef?.overlayElement.querySelector<HTMLElement>('.hx-dropdown-panel');
    requestAnimationFrame(() => {
      if (this.destroyRef.destroyed || !this.isOpen() || !panel?.isConnected) {
        return;
      }

      const searchInput = panel.querySelector<HTMLInputElement>('.hx-dropdown-search input');
      if (searchInput) {
        searchInput.focus();
        return;
      }

      panel.focus();
    });
  }

  /** Keeps the highlighted option visible while navigating with the arrow keys. */
  private readonly scrollActiveOptionIntoView = effect(() => {
    const activeId = this.activeOptionDomId();
    if (!this.isOpen() || !activeId) {
      return;
    }

    requestAnimationFrame(() => {
      this.overlayRef?.overlayElement
        ?.querySelector<HTMLElement>(`#${CSS.escape(activeId)}`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  });

  // ============================================================================
  // SEARCH
  // ============================================================================

  /** Handle search input changes */
  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setSearchQuery(input.value);
  }

  /** Apply a search query and reset the keyboard highlight to the first match. */
  private setSearchQuery(query: string): void {
    this.searchQuery.set(query);
    this.searchChange.emit(query);
    this.focusedIndex.set(this.navigableOptions().length > 0 ? 0 : -1);

    if (query) {
      // Keep the search box in sync when the query was seeded from the trigger (type-to-open).
      const panel = this.overlayRef?.overlayElement.querySelector<HTMLElement>('.hx-dropdown-panel');
      requestAnimationFrame(() => {
        if (this.destroyRef.destroyed || !this.isOpen() || !panel?.isConnected || this.searchQuery() !== query) {
          return;
        }
        const searchInput = panel.querySelector<HTMLInputElement>('.hx-dropdown-search input');
        if (searchInput && searchInput.value !== query) {
          searchInput.value = query;
          searchInput.focus();
          searchInput.setSelectionRange(query.length, query.length);
        }
      });
    }
  }

  /**
   * Handles keys inside the search box.
   *
   * The search input stops `keydown` propagation, so the full panel key handling is delegated from here: arrow keys
   * move the highlight, Enter selects it (closing the panel for single-select, keeping it open for multi-select),
   * Escape closes and Tab commits the current selection before moving to the next control.
   */
  protected onSearchKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
      case 'Escape':
        this.onPanelKeyDown(event);
        return;
      case 'Tab':
        if (!event.shiftKey) {
          if (this.searchQuery()) {
            return;
          }
          this.onPanelKeyDown(event);
          return;
        } else {
          this.handleTabOut(event);
          return;
        }
      case 'Enter':
        event.preventDefault();
        if (this.commitActiveOption()) {
          return;
        }
        this.commitFreeTextFromSearch();
        return;
    }
  }

  /** Commit the typed text as a free-text value when no option matched. */
  private commitFreeTextFromSearch(): void {
    if (!this.freeTextMode()) {
      return;
    }

    const query = this.searchQuery().trim();
    if (!query) {
      return;
    }

    this.commitFreeText(query);
  }

  /** Clear search query and refocus the search input */
  clearSearch(event?: Event): void {
    event?.stopPropagation();
    this.searchQuery.set('');
    this.searchChange.emit('');
    requestAnimationFrame(() => {
      const searchInput = this.overlayRef?.overlayElement?.querySelector<HTMLInputElement>('.hx-dropdown-search input');
      searchInput?.focus();
    });
  }

  /** Handle keyboard interactions on the clear search button */
  protected onClearButtonKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();
    switch (event.key) {
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          const searchInput =
            (event.currentTarget as HTMLElement)
              ?.closest('.hx-dropdown-search')
              ?.querySelector<HTMLInputElement>('input') ??
            this.overlayRef?.overlayElement?.querySelector<HTMLInputElement>('.hx-dropdown-search input');
          searchInput?.focus();
        } else {
          if (this.navigableOptions().length > 0 && this.focusedIndex() < 0) {
            this.focusedIndex.set(0);
          }
          this.overlayRef?.overlayElement?.querySelector<HTMLElement>('.hx-dropdown-panel')?.focus();
        }
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.clearSearch();
        return;
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
        this.onPanelKeyDown(event);
        this.overlayRef?.overlayElement?.querySelector<HTMLElement>('.hx-dropdown-panel')?.focus();
        return;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        return;
    }
  }

  /** Commit a free-text value as the current selection */
  protected commitFreeText(value: string): void {
    if (!this.freeTextMode()) {
      return;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    this.updateValue(trimmed as unknown as T);
    this.clearSearch();
    this.closeDropdown();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /** Update the internal value and notify Forms API */
  private updateValue(value: T | T[] | null): void {
    this.internalValue.set(value);
    this.onChange(value);
    this.valueChange.emit(value);
  }

  /** Helper to check if an item is an option group */
  protected isOptionGroup(item: HxOption<T> | HxOptionGroup<T>): item is HxOptionGroup<T> {
    return 'options' in item && Array.isArray((item as HxOptionGroup<T>).options);
  }

  /** Cast item to HxOption (for template use) */
  protected asOption(item: HxOption<T> | HxOptionGroup<T>): HxOption<T> {
    return item as HxOption<T>;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /** Programmatically open the dropdown */
  open(): void {
    this.openDropdown();
  }

  /** Programmatically close the dropdown */
  close(): void {
    this.closeDropdown();
  }

  /** Toggle the dropdown */
  toggle(): void {
    this.toggleDropdown();
  }

  /** Get the current value */
  getValue(): T | T[] | null {
    return this.internalValue();
  }
}
